import { Box, Flex, Text } from '@chakra-ui/layout';
import { Button, Menu, MenuButton, MenuItem, MenuList, Tooltip } from '@chakra-ui/react';
import { ROUTE_BOOKING_PAGE } from '@/constants/routes';
import { useTranslation } from 'react-i18next';
import { BiHide } from 'react-icons/bi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { HiOutlineClipboardCopy } from 'react-icons/hi';
import { IoOpenOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import useContent from './useContent';

interface IContentProps {
	isPreviewEnabled: boolean;
	togglePreview: () => void;
}

export default function Content(props: IContentProps) {
	const { id } = useContent();

	return (
		<Box
			display={{
				base: props.isPreviewEnabled ? 'block' : 'none',
				md: 'block',
			}}
			m={{
				base: 4,
				md: 4,
			}}
			borderRadius="md"
			mt={{
				base: 4,
				md: 8,
			}}
			pb={{
				base: 0,
				md: 4,
			}}
			pr={{
				base: 0,
				md: 4,
			}}
			overflow="auto"
			position="relative"
		>
			<Preview
				togglePreview={props.togglePreview}
				isPreviewEnabled={props.isPreviewEnabled}
				url={window.location.origin + ROUTE_BOOKING_PAGE.replace(':id', id)}
			/>
		</Box>
	);
}

function Preview(props: { url: string; isPreviewEnabled: boolean; togglePreview: () => void }) {
	return (
		<Box
			resize="both"
			minH="sm"
			minW="xs"
			maxW="100%"
			maxH="100%"
			borderRadius="md"
			overflow="hidden"
			boxShadow={{
				base: 'lg',
				sm: 'md',
			}}
			position="relative"
			backgroundColor="white"
			h="100%"
			display="grid"
			w="100%"
			gridTemplateRows="auto auto 1fr"
		>
			<OSXHeader url={props.url} isPreviewEnabled={props.isPreviewEnabled} togglePreview={props.togglePreview} />
			<BrowserNavigationBar url={props.url} />
			<Box
				id="booking-page-preview"
				as="iframe"
				margin="auto"
				transform={{
					base: 'scale(0.6)',
					sm: 'scale(0.6)',
					md: 'scale(0.5)',
					lg: 'scale(0.8)',
				}}
				width={{
					base: '166%',
					sm: '166%',
					md: '200%',
					lg: '125%',
				}}
				height={{
					base: '166%',
					sm: '166%',
					md: '200%',
					lg: '125%',
				}}
				transformOrigin="top left"
				src={props.url}
				title="Preview"
				frameBorder="0"
				overflow="hidden"
			/>
		</Box>
	);
}

interface IoSXHeaderProps {
	url: string;
	isPreviewEnabled: boolean;
	togglePreview: () => void;
}

function OSXHeader(props: IoSXHeaderProps) {
	const { t } = useTranslation();
	return (
		<Box display="flex" alignItems="center" bg="gray.200" p="1" position="relative">
			<Box display="inline-block" bg="red.400" w="3" h="3" borderRadius="full" mr="2" ml="1" />
			<Box display="inline-block" bg="yellow.400" w="3" h="3" borderRadius="full" mr="2" ml="0" />
			<Box display="inline-block" bg="green.400" w="3" h="3" borderRadius="full" mr="2" ml="0" />
			<Tooltip label={t('This is how your customers will see your booking page.')}>
				<Text cursor="help" flex="1" textAlign="center" fontWeight="600" fontSize="xs">
					{t('Booking page preview')}
				</Text>
			</Tooltip>

			<Box ml="auto">
				<Menu>
					<MenuButton size="xs" as={Button} rightIcon={<BsThreeDotsVertical />}>
						{t('Menu')}
					</MenuButton>
					<MenuList>
						<MenuItem
							icon={<BiHide />}
							display={{
								base: 'flex',
								md: 'none',
							}}
							onClick={props.togglePreview}
						>
							{t('Hide preview')}
						</MenuItem>
						<MenuItem
							icon={<HiOutlineClipboardCopy />}
							onClick={() => {
								navigator.clipboard.writeText(props.url).then(() => {
									toast.success(t('Copied to clipboard') as string);
								});
							}}
						>
							{t('Copy link')}
						</MenuItem>
						<MenuItem
							icon={<IoOpenOutline />}
							onClick={() => {
								window.open(props.url, '_blank');
							}}
						>
							{t('Open preview on a new tab')}
						</MenuItem>
					</MenuList>
				</Menu>
			</Box>
		</Box>
	);
}

function BrowserNavigationBar(props: { url: string }) {
	const { t } = useTranslation();
	return (
		<Box
			alignItems="center"
			bg="gray.100"
			p="1"
			position="relative"
			overflow="hidden"
			whiteSpace="nowrap"
			textOverflow="ellipsis"
			wordBreak="break-all"
			cursor="pointer"
			onClick={() => {
				navigator.clipboard.writeText(props.url).then(() => {
					toast.success(t('Copied to clipboard') as string);
				});
			}}
		>
			<Flex background="white" borderRadius="lg" alignItems="center" justifyContent="space-between" py="1" px="1">
				<Tooltip label={t('Share this link with your customers to get bookings.')} hasArrow>
					<Text
						whiteSpace="nowrap"
						textOverflow="ellipsis"
						wordBreak="break-all"
						overflow="hidden"
						pl="2"
						title={t('Copy link')}
						fontSize={{
							base: 'xx-small',
							md: '2xs',
							lg: 'xs',
							xl: 'sm',
						}}
						_hover={{
							color: 'blue.500',
							textDecoration: 'underline',
						}}
					>
						{props.url}
					</Text>
				</Tooltip>
			</Flex>
		</Box>
	);
}
